<script setup lang="ts">
import {computed, ref} from "vue";
import {ProgramBookable} from "@/program/interfaces/ProgramBookable";
import {Skeleton} from "@/core/components/ui/skeleton";
import useCustomerAppointments from "@/appointment/composables/useCustomerAppointments";
import AgendaAppointmentDetail from "@/agenda/components/AgendaAppointmentDetail.vue";
import {AgendaAppointment} from "@/core/interfaces/AgendaAppointment";
import {toAgendaAppointment} from "@/agenda/lib/toAgendaAppointment";
import dateHelper from "@/core/helpers/dateHelper";
import {CalendarDays} from "@lucide/vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import StartJourneyModal
    from "@/appointment/components/consumer-appointment-timeline/start-journey/StartJourneyModal.vue";
import Customer from "@/customer/interfaces/Customer";
import CustomerCertificationDialog from "@/customer/components/customer-certification/CustomerCertificationDialog.vue";

const props = defineProps<{
    bookable: ProgramBookable,
    programmableId: number,
    programmableType: string;
    customer: Customer
}>();

const emits = defineEmits<{
    (e: 'certified'): void
}>();

const {data: appointments, isLoading, mutate} = useCustomerAppointments(props.customer.id);
const journeyModalOpen = ref(false);
const certificationOpen = ref(false);
const appointmentOpen = ref(false);

/**
 * Taken when the button is clicked: the button names the program's next
 * appointment, which a refetch may replace while a dialog acts on this one.
 */
const openedAppointment = ref<AgendaAppointment | null>(null);

const openAppointment = () => {
    if (programAppointment.value) {
        openedAppointment.value = toAgendaAppointment(programAppointment.value);
        appointmentOpen.value = true;
    }
};

/**
 * Get the next appointment for the current program.
 */
const programAppointment = computed(() => {
    if (!appointments.value?.length) {
        return null;
    }

    return appointments.value.find((_appointment) => {
        return _appointment.appointmentable_id === props.programmableId
            && _appointment.appointmentable_type === props.programmableType;
    }) ?? null;
});

</script>

<template>
  <div>
    <Skeleton v-if="isLoading" class="h-8 w-40" />
    <div v-if="programAppointment">
      <BaseButton density="comfortable" class="text-primary text-base inline-flex items-center" @click="openAppointment">
        <CalendarDays class="size-5 mr-1 shrink-0" />
        Prochain RDV le
        {{ dateHelper.shortFormat(programAppointment.start_at, 'DD MMMM à HH:mm') }}
      </BaseButton>
    </div>
    <div v-else-if="programmableType === 'support'">
      <BaseButton density="comfortable" @click="journeyModalOpen = true">
        <CalendarDays class="size-5 mr-1 shrink-0" />
        Rendez-vous
      </BaseButton>
      <StartJourneyModal
        v-model:open="journeyModalOpen"
        :customer="customer"
        :support-program-id="programmableId"
        @update:open="(value) => !value && mutate()"
        @open-certification-dialog="mutate(); journeyModalOpen = false; certificationOpen = true"
      />
    </div>
    <!-- Mounted on the first click: its dialogs load the team and the types. -->
    <AgendaAppointmentDetail
      v-if="openedAppointment"
      v-model:open="appointmentOpen"
      :appointment="openedAppointment"
      @changed="mutate()"
    />
    <CustomerCertificationDialog v-model:open="certificationOpen" :customer="customer" @certified="emits('certified')" />
  </div>
</template>
